import { useEffect, useMemo } from "react";
import { addMonths } from "date-fns";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { cn, formatDate, parseDateParam } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MaintenanceTask, Transaction } from "@/lib/types";
import { Mixpanel } from "@/lib/mixpanel";
import AddExpenseButton from "../../components/add-expense-button";
import SelectingBoatButton from "../../components/select-vessel-button";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";
interface HeaderProps {
  transactions: Transaction[];
  isDateSelected: boolean;
  setIsDateSelected: (value: boolean) => void;
  tasks: MaintenanceTask[];
}

const dots = {
  overdue: "bg-[#F6B11A]",
  nextDate: "bg-[#26C2DD]",
};

const Header = ({
  transactions,
  isDateSelected,
  setIsDateSelected,
  tasks,
}: HeaderProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchDate = searchParams.get("date");
  const date = useMemo(() => parseDateParam(searchDate), [searchDate]);
  const canWriteExpenses = useHasPermission(PERMISSIONS.Expenses.Write);

  useEffect(() => {
    if (!searchParams.has("date")) {
      setSearchParams((prev) => {
        prev.set("date", formatDate(date, "yyyy-MM-dd"));
        return prev;
      });
    }
  }, [date, searchParams, setSearchParams]);

  const handleMonthChange = (increment: number) => {
    setSearchParams((prev) => {
      const newDate = addMonths(parseDateParam(prev.get("date")), increment);
      prev.set("date", formatDate(newDate, "yyyy-MM-dd"));
      return prev;
    });
  };

  const handleCalendarSelect = (value: Date | undefined) => {
    setSearchParams((prev) => {
      if (value) {
        prev.set("date", formatDate(value, "yyyy-MM-dd"));
      }
      return prev;
    });
    setIsDateSelected(!!value);
  };

  return (
    <div className="flex flex-col items-center justify-center px-5">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <SelectingBoatButton />
        {canWriteExpenses && (
          <AddExpenseButton defaultDate={isDateSelected ? date : undefined} />
        )}
      </div>

      <div className="mt-[15px] flex w-full flex-col items-center justify-center">
        <button
          data-testid="calendar-button"
          onClick={() => {
            navigate("/");
            Mixpanel.track("Navigate to Home");
          }}
          className={cn(
            "transaction-calendar-button flex w-full items-center justify-between rounded-md border border-y-white/10 border-l-[#406f7e] border-r-[#63919f] text-left font-normal shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)]",
            !date && "text-muted-foreground"
          )}
        >
          <div
            data-testid="previous-month-button"
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              handleMonthChange(-1);
              Mixpanel.track("Previous Month Button Clicked");
            }}
            className="flex h-9 items-center justify-center px-2 text-white/65 transition-all duration-200 hover:bg-transparent hover:text-white"
          >
            <CaretLeft />
          </div>
          <span
            data-testid="selected-month-value"
            className="text-base font-medium leading-4 text-white"
          >
            {formatDate(date, "MMMM yyyy")}
          </span>
          <div
            data-testid="next-month-button"
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              handleMonthChange(1);
              Mixpanel.track("Next Month Button Clicked");
            }}
            className="flex h-9 items-center justify-center px-2 text-white/65 transition-all duration-200 hover:bg-transparent hover:text-white"
          >
            <CaretRight />
          </div>
        </button>

        <Calendar
          className="mt-[5px] w-full rounded-lg border-[1.5px] border-white/[4%] p-[10px] shadow-[0px_1px_4px_0px_#00000040]"
          onMonthChange={(date) =>
            date &&
            setSearchParams((prev) => {
              prev.set("date", formatDate(date, "yyyy-MM-dd"));
              return prev;
            })
          }
          month={date}
          dayFooter={(date) => {
            const hasExpense = transactions
              .filter(
                (item) => item.type === "Expense" || item.type === "Income"
              )
              .some((item) => {
                const expenseDate = new Date(item.transactionDate);
                return (
                  formatDate(expenseDate, "yyyy-MM-dd") ===
                  formatDate(date, "yyyy-MM-dd")
                );
              });

            const taskDotForDate: {
              value: boolean;
              type: "overdue" | "nextDate" | null;
            } = (() => {
              const selectedDate = formatDate(date, "yyyy-MM-dd");
              const tasksOnDate = tasks.filter((task) => {
                const dueMatch =
                  task.taskDetail.dueDate &&
                  formatDate(task.taskDetail.dueDate, "yyyy-MM-dd") ===
                    selectedDate;
                const nextMatch =
                  task.taskDetail.nextDate &&
                  formatDate(task.taskDetail.nextDate, "yyyy-MM-dd") ===
                    selectedDate;
                return dueMatch || nextMatch;
              });
              if (tasksOnDate.length === 0) return { value: false, type: null };
              const hasOverdue = tasksOnDate.some(
                (t) =>
                  t.taskDetail.timeToDueScore != null &&
                  t.taskDetail.timeToDueScore < 0
              );
              return {
                value: true,
                type: hasOverdue ? "overdue" : "nextDate",
              };
            })();

            return hasExpense || taskDotForDate.value ? (
              <div
                className="mt-1 flex flex-row items-center justify-center gap-1"
                data-testid={`calendar-day-dot-${formatDate(date, "yyyy-MM-dd")}`}
              >
                {taskDotForDate.value && taskDotForDate.type && (
                  <span
                    className={cn(
                      "flex h-1 w-1 min-w-1 rounded-full",
                      dots[taskDotForDate.type]
                    )}
                  />
                )}

                {hasExpense && (
                  <span className="flex h-1 w-1 min-w-1 rounded-full bg-[#1BE368]" />
                )}
              </div>
            ) : null;
          }}
          hideCaption
          mode="single"
          selected={isDateSelected ? date : undefined}
          onSelect={handleCalendarSelect}
        />
      </div>
    </div>
  );
};

export default Header;
